import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Base prices in the data file are in INR. All conversions happen client-side
// with a fixed rate table (kept in one place so it's easy to keep fresh).
export const CURRENCIES = {
  INR: { code: "INR", label: "INR · ₹", symbol: "₹", rate: 1, locale: "en-IN", country: "India" },
  USD: { code: "USD", label: "USD · $", symbol: "$", rate: 0.012, locale: "en-US", country: "United States" },
  GBP: { code: "GBP", label: "GBP · £", symbol: "£", rate: 0.0093, locale: "en-GB", country: "United Kingdom" },
  EUR: { code: "EUR", label: "EUR · €", symbol: "€", rate: 0.011, locale: "en-IE", country: "Europe" },
  AED: { code: "AED", label: "AED · د.إ", symbol: "د.إ", rate: 0.044, locale: "en-AE", country: "United Arab Emirates" },
  AUD: { code: "AUD", label: "AUD · A$", symbol: "A$", rate: 0.019, locale: "en-AU", country: "Australia" },
  CAD: { code: "CAD", label: "CAD · C$", symbol: "C$", rate: 0.017, locale: "en-CA", country: "Canada" },
  SGD: { code: "SGD", label: "SGD · S$", symbol: "S$", rate: 0.016, locale: "en-SG", country: "Singapore" },
};

const STORAGE_KEY = "anabiya_currency_v1";

// Default currency for the shop is USD. Users can override via switcher.
// Only respects a previously-saved manual selection.
function autoDetectCurrency() {
  return "USD";
}

// Rounds sensibly per currency
function pretty(v, code) {
  if (code === "INR") return Math.round(v);
  // Round non-INR up to a .99 psychology-friendly figure
  const whole = Math.max(1, Math.round(v));
  return whole - 0.01;
}

const Ctx = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [code, setCode] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && CURRENCIES[saved]) return saved;
    } catch {}
    return autoDetectCurrency();
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {}
  }, [code]);

  const currency = CURRENCIES[code] || CURRENCIES.USD;

  const convert = (inr) => pretty(Number(inr) * currency.rate, code);

  const format = (inr) => {
    const v = convert(inr);
    try {
      return new Intl.NumberFormat(currency.locale, {
        style: "currency",
        currency: currency.code,
        minimumFractionDigits: code === "INR" ? 0 : 2,
        maximumFractionDigits: code === "INR" ? 0 : 2,
      }).format(v);
    } catch {
      return `${currency.symbol}${v}`;
    }
  };

  /**
   * formatMoney(amount, currencyCode)
   * ----------------------------------------------------------------
   * Format an amount that is ALREADY in the given currency — no
   * conversion, no psychology rounding, exact to 2 decimals.
   * Use this whenever you are displaying real settled amounts stored
   * on order / payment records where the currency is authoritative
   * (Admin Orders, Order Detail, PayPal capture summaries).
   *
   * The storefront `format(inr)` above stays as-is for INR-based
   * product prices that need currency conversion for the shopper.
   */
  const formatMoney = (amount, currencyCode) => {
    const c = CURRENCIES[currencyCode] || CURRENCIES.USD;
    const n = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(c.locale, {
        style: "currency",
        currency: c.code,
        minimumFractionDigits: c.code === "INR" ? 0 : 2,
        maximumFractionDigits: c.code === "INR" ? 0 : 2,
      }).format(n);
    } catch {
      return `${c.symbol}${n.toFixed(c.code === "INR" ? 0 : 2)}`;
    }
  };

  const value = useMemo(
    () => ({ code, setCode, currency, convert, format, formatMoney, all: CURRENCIES }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCurrency = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useCurrency must be inside CurrencyProvider");
  return c;
};
